import {
  mountBackgammonBoard,
  readStateFromBoardDom,
  clearRemotePresence,
  showRemotePiecePick,
  showRemotePieceHover,
} from "../game/board.js";
import { parseGameState } from "../game/state.js";
import { createPairingGame, fetchGameById } from "../api/games.js";
import { subscribeGame } from "../realtime/gameChannel.js";
import { DICE_SPIN_MS, isDiceSpinning, playDiceRollAnimation } from "./dice.js";
import { mountChat } from "./chat.js";

/**
 * @param {HTMLElement} root
 * @param {object} opts
 * @param {import('@supabase/supabase-js').SupabaseClient} opts.supabase
 * @param {string} opts.userId
 * @param {string} opts.pairingId
 * @param {string | null} opts.gameId
 * @param {'new' | 'resume'} opts.mode
 * @param {() => void} opts.onLeave
 */
export async function mountGameRoom(root, opts) {
  const { supabase, userId, pairingId, gameId: initialGameId, mode, onLeave } = opts;

  let gameId = initialGameId;
  if (mode === "new" || !gameId) {
    gameId = await createPairingGame(supabase, pairingId);
  }

  const { game, error } = await fetchGameById(supabase, gameId);
  if (error || !game) {
    root.textContent = `Could not load game: ${error?.message ?? "unknown"}`;
    return { destroy() {} };
  }

  const rawSv = game.state_version ?? game.stateVersion ?? 1;
  let serverVersion = typeof rawSv === "number" ? rawSv : Number(rawSv) || 1;

  const header = document.createElement("div");
  header.className = "game-room-header";
  header.innerHTML = `<span>Game</span>`;
  const leaveBtn = document.createElement("button");
  leaveBtn.type = "button";
  leaveBtn.textContent = "Back to pairs";
  leaveBtn.addEventListener("click", () => onLeave());
  header.appendChild(leaveBtn);

  const body = document.createElement("div");
  body.className = "game-room-body";

  const boardCol = document.createElement("div");
  boardCol.className = "game-room-board";

  let saveTimer = 0;
  let hoverThrottle = 0;
  /** @type {{ root: HTMLElement; getState: () => import('../game/state.js').GameState; setState: (s: object) => void; destroy: () => void } | null} */
  let boardApi = null;
  /** @type {ReturnType<typeof subscribeGame> | null} */
  let gameChannel = null;
  /** @type {{ fromZone: string, stackIndex: number, color: 'light' | 'dark', toZone?: string } | null} */
  let remoteDrag = null;

  function reapplyRemoteDrag() {
    if (!boardApi || !remoteDrag) return;
    showRemotePiecePick(boardApi.root, remoteDrag);
    if (remoteDrag.toZone) {
      showRemotePieceHover(boardApi.root, remoteDrag.toZone);
    }
  }

  function applyRemoteState(raw) {
    if (!boardApi) return;
    const parsed = parseGameState(raw);
    const current = boardApi.getState();
    if (isDiceSpinning(boardApi.root, "brown")) {
      parsed.dice.brown = current.dice.brown;
    }
    if (isDiceSpinning(boardApi.root, "white")) {
      parsed.dice.white = current.dice.white;
    }
    boardApi.setState(parsed);
    reapplyRemoteDrag();
  }

  /**
   * @param {import('../realtime/gameChannel.js').DiceRollPayload} payload
   */
  function handleRemoteDiceRoll(payload) {
    if (!boardApi) return;
    const player = payload.player;
    const values = payload.values;
    const durationMs = payload.durationMs ?? DICE_SPIN_MS;
    playDiceRollAnimation(boardApi.root, player, values, durationMs);
  }

  /**
   * @param {{ reverted: boolean }} move
   */
  function applyPieceMove(move) {
    if (!boardApi) return;
    remoteDrag = null;
    clearRemotePresence(boardApi.root);
  }

  async function pushStateToServer() {
    if (!boardApi) return;
    const domState = readStateFromBoardDom(boardApi.root);
    const payload = {
      points: domState.points,
      bar: domState.bar,
      trayLeft: domState.trayLeft,
      trayRight: domState.trayRight,
      dice: domState.dice,
    };
    const nextVersion = serverVersion + 1;
    const { data, error: upErr } = await supabase
      .from("games")
      .update({
        state_json: payload,
        state_version: nextVersion,
      })
      .eq("id", gameId)
      .eq("state_version", serverVersion)
      .select("id, state_version")
      .maybeSingle();

    if (upErr) {
      console.warn("save failed", upErr);
      return;
    }
    if (!data) {
      const refetch = await fetchGameById(supabase, gameId);
      if (refetch.game?.state_json) {
        serverVersion = /** @type {number} */ (refetch.game.state_version ?? serverVersion);
        applyRemoteState(refetch.game.state_json);
      }
      return;
    }
    serverVersion = /** @type {number} */ (data.state_version ?? nextVersion);
  }

  function scheduleSave() {
    window.clearTimeout(saveTimer);
    saveTimer = window.setTimeout(() => {
      void pushStateToServer();
    }, 450);
  }

  gameChannel = await subscribeGame(supabase, gameId, userId, {
    onDiceRoll: handleRemoteDiceRoll,
    onPiecePick: (pick) => {
      remoteDrag = {
        fromZone: pick.fromZone,
        stackIndex: pick.stackIndex,
        color: pick.color,
      };
      if (boardApi) showRemotePiecePick(boardApi.root, pick);
    },
    onPieceHover: (hover) => {
      if (!boardApi) return;
      if (remoteDrag) remoteDrag.toZone = hover.toZone;
      window.clearTimeout(hoverThrottle);
      hoverThrottle = window.setTimeout(() => {
        showRemotePieceHover(boardApi.root, hover.toZone);
      }, 80);
    },
    onPieceMove: (payload) => {
      applyPieceMove(payload);
    },
    onGameRow: (row) => {
      const rawV = row.state_version ?? row.stateVersion ?? 0;
      const v = typeof rawV === "number" ? rawV : Number(rawV) || 0;
      if (v > serverVersion && row.state_json) {
        serverVersion = v;
        applyRemoteState(row.state_json);
      }
    },
  });

  boardApi = mountBackgammonBoard(boardCol, {
    initialState: parseGameState(game.state_json),
    onLocalChange: () => scheduleSave(),
    onPiecePick: (pick) => {
      gameChannel?.send("piece_pick", {
        fromZone: pick.fromZone,
        stackIndex: pick.stackIndex,
        color: pick.color,
      });
    },
    onPieceHover: (toZone) => {
      window.clearTimeout(hoverThrottle);
      hoverThrottle = window.setTimeout(() => {
        gameChannel?.send("piece_hover", { toZone });
      }, 80);
    },
    onPieceMove: ({ pick, toZone, reverted }) => {
      applyPieceMove({
        fromZone: pick.fromZone,
        stackIndex: pick.stackIndex,
        color: pick.color,
        reverted,
      });
      gameChannel?.send("piece_move", {
        fromZone: pick.fromZone,
        stackIndex: pick.stackIndex,
        color: pick.color,
        toZone,
        reverted,
      });
    },
    onRollStart: (player, values, rollId) => {
      gameChannel?.send("dice_roll", {
        rollId,
        player,
        values,
        durationMs: DICE_SPIN_MS,
      });
    },
    onRollComplete: () => scheduleSave(),
  });

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      boardApi?.setState(parseGameState(game.state_json));
    });
  });

  const chatHost = boardApi?.chatMount;
  const destroyChat = chatHost
    ? mountChat(chatHost, { supabase, gameId, currentUserId: userId })
    : () => {};

  body.appendChild(boardCol);
  root.appendChild(header);
  root.appendChild(body);

  return {
    destroy() {
      window.clearTimeout(saveTimer);
      window.clearTimeout(hoverThrottle);
      gameChannel?.destroy();
      destroyChat();
      boardApi?.destroy();
      root.replaceChildren();
    },
  };
}
